import { Option } from 'chakra-multiselect'
import { useCallback, useEffect, useState } from 'react'
import { useToast } from '@chakra-ui/react'
import {
  calculateMonthsDifference,
  isDateRangeInvalid,
  isMonthDateValid,
} from '../utils/calculateMonthsDifference'
import { Diagnosis } from '../types/FirestoreUser'
import { ProgressionQueryParams } from '../types/ProgressionQuery'

export type ProgressionQueryFormState = {
  minAge: string
  maxAge: string
  type: string
  direction: string
  minChange: string
  fromDate: string
  toDate: string
  country: string
  gender: string
  diagnosis: Option[]
  monthsSinceDiagnosis: string
}

const initialFormState: ProgressionQueryFormState = {
  minAge: '',
  maxAge: '',
  type: 'alsfrs',
  direction: 'up',
  minChange: '0',
  fromDate: '',
  toDate: '',
  country: '',
  gender: '',
  diagnosis: [],
  monthsSinceDiagnosis: '',
}

export default function useProgressionQueryForm() {
  const [formState, setFormState] =
    useState<ProgressionQueryFormState>(initialFormState)
  const toast = useToast()

  // Initialize default date range (6 months inclusive)
  useEffect(() => {
    const today = new Date()
    const currentYear = today.getFullYear()
    const currentMonth = (today.getMonth() + 1).toString().padStart(2, '0')

    const fiveMonthsAgo = new Date(today)
    fiveMonthsAgo.setMonth(today.getMonth() - 5)
    const fromYear = fiveMonthsAgo.getFullYear()
    const fromMonth = (fiveMonthsAgo.getMonth() + 1).toString().padStart(2, '0')

    setFormState((prev) => ({
      ...prev,
      toDate: `${currentYear}-${currentMonth}`,
      fromDate: `${fromYear}-${fromMonth}`,
    }))
  }, [])

  const updateField = useCallback(
    <K extends keyof ProgressionQueryFormState>(
      field: K,
      value: ProgressionQueryFormState[K]
    ) => {
      setFormState((prev) => ({ ...prev, [field]: value }))
    },
    []
  )

  const validate = (): boolean => {
    if (
      !isMonthDateValid(formState.fromDate) ||
      !isMonthDateValid(formState.toDate)
    ) {
      toast({
        title: 'Invalid Date Range',
        description:
          'Please select both a year and month for From and To dates.',
        status: 'error',
        isClosable: true,
      })
      return false
    }

    if (isDateRangeInvalid(formState.fromDate, formState.toDate)) {
      toast({
        title: 'Invalid Date Range',
        description: "The 'To' date must be after or equal to the 'From' date.",
        status: 'error',
        isClosable: true,
      })
      return false
    }

    return true
  }

  const buildParams = (): ProgressionQueryParams => {
    const months = calculateMonthsDifference(
      formState.fromDate,
      formState.toDate
    )

    return {
      direction: formState.direction as 'up' | 'down',
      maxAge: formState.maxAge ? parseInt(formState.maxAge) : undefined,
      minAge: formState.minAge ? parseInt(formState.minAge) : undefined,
      minChange: parseInt(formState.minChange),
      months,
      fromDate: formState.fromDate,
      toDate: formState.toDate,
      type: formState.type as 'alsfrs' | 'alssq' | 'combined',
      country: formState.country || undefined,
      gender: formState.gender || undefined,
      diagnosis:
        formState.diagnosis.length > 0
          ? formState.diagnosis.map((d) => d.value as Diagnosis)
          : undefined,
      monthsSinceDiagnosis: formState.monthsSinceDiagnosis
        ? parseInt(formState.monthsSinceDiagnosis)
        : undefined,
    }
  }

  return {
    formState,
    updateField,
    validate,
    buildParams,
  }
}
